import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { overtimeApi, overtimeKeys } from "@/features/overtime/api";
import type {
  LineCorrection,
  RectificationApproval,
  RectificationRequest,
  ReviewPeriod,
} from "@/features/overtime/api";
import type { QueryParams } from "@/lib/api/client";
import { dashboardKeys } from "@/features/dashboard/hooks";

export function useOvertimeDays(filters: QueryParams) {
  return useQuery({
    queryKey: overtimeKeys.days(filters),
    queryFn: () => overtimeApi.days(filters),
  });
}

export function useMyOvertimeDays(filters: QueryParams = {}) {
  return useQuery({
    queryKey: [...overtimeKeys.myDays(), filters],
    queryFn: () => overtimeApi.myDays(filters),
  });
}

/**
 * One month of the signed-in person's own attendance.
 *
 * The previous month's rows stay on screen while the next one loads, so
 * stepping through the history does not blink the table empty each time.
 */
export function useMyAttendance(month: string) {
  return useQuery({
    queryKey: overtimeKeys.myAttendance(month),
    queryFn: () => overtimeApi.myAttendance(month),
    placeholderData: keepPreviousData,
  });
}

export function useMyOvertimeBalance() {
  return useQuery({ queryKey: overtimeKeys.myBalance(), queryFn: overtimeApi.myBalance });
}

export function useOvertimeBalances() {
  return useQuery({ queryKey: overtimeKeys.balances(), queryFn: overtimeApi.balances });
}

export function useAttendanceImports() {
  return useQuery({ queryKey: overtimeKeys.imports(), queryFn: overtimeApi.imports });
}

export function useUnmatchedBadges() {
  return useQuery({ queryKey: overtimeKeys.unmatched(), queryFn: overtimeApi.unmatchedBadges });
}

export function useOvertimeLedger(userId?: string) {
  return useQuery({
    queryKey: overtimeKeys.ledger(userId),
    queryFn: () => overtimeApi.ledger(userId),
  });
}

function useOvertimeMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: overtimeKeys.all });
      // The rail counts overtime waiting for a decision.
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useImportAttendance() {
  return useOvertimeMutation((file: File) => overtimeApi.importFile(file));
}

export function useMapBadge() {
  return useOvertimeMutation(({ badge, userId }: { badge: string; userId: string }) =>
    overtimeApi.mapBadge(badge, userId),
  );
}

export function useAdjustOvertime() {
  return useOvertimeMutation(
    ({ dayId, minutes, reason }: { dayId: string; minutes: number; reason: string }) =>
      overtimeApi.adjust(dayId, minutes, reason),
  );
}

export function useClearOvertimeAdjustment() {
  return useOvertimeMutation((dayId: string) => overtimeApi.clearAdjustment(dayId));
}

export function useAddOvertimeLedgerEntry() {
  return useOvertimeMutation(
    (payload: { user_id: string; minutes: number; reason: string }) =>
      overtimeApi.addLedgerEntry(payload),
  );
}

// ── HR checking the days of an upload ───────────────────────────────────────

/** The period's dates and where each stands. No period means the latest upload's. */
export function useAttendanceReview(period: Partial<ReviewPeriod>) {
  return useQuery({
    queryKey: overtimeKeys.review(period),
    queryFn: () => overtimeApi.review(period),
    placeholderData: keepPreviousData,
  });
}

/** Everybody's line for one date. */
export function useReviewDay(date: string | null) {
  return useQuery({
    queryKey: overtimeKeys.reviewDay(date ?? ""),
    queryFn: () => overtimeApi.reviewDay(date as string),
    enabled: Boolean(date),
    placeholderData: keepPreviousData,
  });
}

export function useConfirmDay() {
  return useOvertimeMutation((date: string) => overtimeApi.confirmDay(date));
}

export function useReopenDay() {
  return useOvertimeMutation((date: string) => overtimeApi.reopenDay(date));
}

export function useConfirmPeriod() {
  return useOvertimeMutation((period: ReviewPeriod & { only_clean: boolean }) =>
    overtimeApi.confirmPeriod(period),
  );
}

export function useSendPeriod() {
  return useOvertimeMutation((period: ReviewPeriod) => overtimeApi.sendPeriod(period));
}

export function useCorrectLine() {
  return useOvertimeMutation(
    ({ date, userId, correction }: { date: string; userId: string; correction: LineCorrection }) =>
      overtimeApi.correctLine(date, userId, correction),
  );
}

// ── Corrections employees asked for ─────────────────────────────────────────

export function useRectifications(status = "pending") {
  return useQuery({
    queryKey: overtimeKeys.rectifications(status),
    queryFn: () => overtimeApi.rectifications(status),
  });
}

export function useApproveRectification() {
  return useOvertimeMutation(({ id, approval }: { id: string; approval: RectificationApproval }) =>
    overtimeApi.approveRectification(id, approval),
  );
}

export function useRejectRectification() {
  return useOvertimeMutation(({ id, reason }: { id: string; reason: string }) =>
    overtimeApi.rejectRectification(id, reason),
  );
}

export function useRequestRectification() {
  return useOvertimeMutation((request: RectificationRequest) =>
    overtimeApi.requestRectification(request),
  );
}

export function useWithdrawRectification() {
  return useOvertimeMutation((id: string) => overtimeApi.withdrawRectification(id));
}
